import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import "./index.css";
import App from './App';
import reportWebVitals from './reportWebVitals';
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import '@fontsource/roboto/900.css';
import '@fontsource/barlow-condensed/200.css';
import '@fontsource/barlow-condensed/300.css';
import '@fontsource/barlow-condensed/400.css';
import '@fontsource/barlow-condensed/500.css';
import '@fontsource/barlow-condensed/600.css';
import '@fontsource/barlow-condensed/700.css';
import '@fontsource/barlow-condensed/800.css';
import '@fontsource/barlow-condensed/900.css';
import '@fontsource-variable/roboto-condensed';
import '@fontsource/special-gothic-condensed-one';
import '@fontsource-variable/oswald'; // Variable font import
import { store } from './app/store';
import { Provider } from 'react-redux';

import './i18n';
import { persistStore } from 'redux-persist';
import { PersistGate } from 'redux-persist/integration/react';
import { SocketProvider } from './app/SocketContext';
import ErrorBoundary from './app/ErrorBoundary';

// Comprehensive history and gesture blocking for Android Chrome
const blockHistoryNavigation = () => {
  // Push a dummy state to the history
  window.history.pushState(null, '', window.location.href);
  
  // Prevent back/forward navigation
  window.addEventListener('popstate', function(event) {
    event.preventDefault();
    event.stopPropagation();
    window.history.pushState(null, '', window.location.href);
  }, { capture: true, passive: false });

  // Prevent refresh gestures and swipe navigation
  window.addEventListener('touchstart', function(event) {
    // Prevent pull-to-refresh on touch devices
    if (event.touches.length > 1) {
      event.preventDefault();
    }
  }, { passive: false });

  window.addEventListener('touchmove', function(event) {
    // Prevent swipe gestures that might trigger navigation
    if (event.touches.length > 1) {
      event.preventDefault();
    }
  }, { passive: false });

  // Block keyboard shortcuts that might cause navigation
  window.addEventListener('keydown', function(event) {
    // Prevent Ctrl+R, F5 (refresh), Alt+Left/Right (back/forward), etc.
    if (
      event.key === 'F5' ||
      (event.ctrlKey && event.key === 'r') ||
      (event.ctrlKey && event.key === 'R') ||
      (event.altKey && event.key === 'ArrowLeft') ||
      (event.altKey && event.key === 'ArrowRight') ||
      (event.metaKey && event.key === 'r') ||
      (event.metaKey && event.key === 'R')
    ) {
      event.preventDefault();
      event.stopPropagation();
      return false;
    }
  }, { capture: true, passive: false });

  // Prevent right-click context menu (which might have refresh/back options)
  window.addEventListener('contextmenu', function(event) {
    event.preventDefault();
    return false;
  }, { passive: false });
};

// Initialize history blocking immediately
blockHistoryNavigation();

// Request persistent storage to prevent IndexedDB eviction in PWA
if (navigator.storage && navigator.storage.persist) {
  navigator.storage.persist().then((granted) => {
    if (granted) {
      console.log('Persistent storage granted - data will not be evicted');
    } else {
      console.warn('Persistent storage denied - data may be evicted under pressure');
    }
  });
}

// Clean up old localStorage data from previous storage backend
try {
  if (localStorage.getItem('persist:root')) {
    localStorage.removeItem('persist:root');
    console.log('Cleaned up old localStorage persist data (migrated to IndexedDB)');
  }
} catch (e) {
  // Ignore - localStorage may not be available
}

// Re-apply on page load
window.addEventListener('load', blockHistoryNavigation);

// Catch all unhandled errors and promise rejections
window.onerror = (message, source, lineno, colno, error) => {
  console.error('Unhandled error:', { message, source, lineno, colno, error });
};

window.onunhandledrejection = (event) => {
  console.error('Unhandled promise rejection:', event.reason);
};

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
const persistor = persistStore(store);
root.render(
  <React.StrictMode>
    <Suspense fallback="loading">
      <PersistGate loading={null} persistor={persistor}>
        <Provider store={store}>
          <SocketProvider>
            <ErrorBoundary fallback={<div>Something went wrong.</div>}>
              <App />
            </ErrorBoundary>
          </SocketProvider>
        </Provider>
      </PersistGate>
    </Suspense>
  </React.StrictMode>
);

// Service worker registration is handled inside the React tree by
// `useServiceWorkerUpdate` (mounted via <UpdatePrompt />) so that update
// lifecycle events can drive a user-facing prompt.

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
